import React, { useEffect, useState, useCallback } from 'react';
import {
    IonContent,
    IonPage,
    IonIcon,
    IonLoading,
    useIonAlert,
    IonSelect,
    IonSelectOption,
    IonAlert,
    IonSearchbar,
} from '@ionic/react';
import {
    addOutline,
    chevronBack,
    chevronForward, downloadOutline,
    lockOpenOutline,
    pencil,
    trash
} from 'ionicons/icons';

import useStore from "../../zustand/useStore";
import {
    actionToDeleteUserPostData,
    actionToGetUsersByFilter,
    actionToChangeUserRole,
    actionToGetAllUsersForExport,
    actionToResetUserPassword
} from "../../apiHelpers/CommonAction";
import { FacebookLoader } from "../../components/Loader/FacebookLoader";
import AddUserModal from "../../components/AddUserModal";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import AddEditUserMembershipData from "../../components/AddEditUserMembershipData";
const ITEMS_PER_PAGE = 10;

// === Debounce helper (JS) ===
const useDebounced = (value, delay = 450) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
};

const AdminUserManagementPage = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const { usersDataState } = useStore();
    const { usersData, loading, totalCount } = usersDataState;

    const pageCount = Math.ceil((totalCount || 0) / ITEMS_PER_PAGE);

    const [presentAlert] = useIonAlert();

    const [showAddUserModalPopup, setShowAddUserModalPopup] = useState(false);
    const [showResetAlert, setShowResetAlert] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [editMemberShipData, setEditMemberShipData] = useState(null);
    const [editUsersData, setEditUsersData] = useState(null);
    const [dataForEditing, setDataForEditing] = useState(null);

    // === NEW: search state + debounced value ===
    const [q, setQ] = useState("");
    const debouncedQ = useDebounced(q, 450);

    // Log only the debounced value (you'll do the API call here)
    useEffect(() => {
        actionToGetUsersByFilter(true, { search_text:debouncedQ, offset: 0 });
    }, [debouncedQ]);

    const handlePasswordReset = async (user) => {
        if (!password || !confirmPassword) {
            presentAlert({ message: "Both fields are required!", buttons: ["OK"] });
            return;
        }
        if (password.length < 8) {
            presentAlert({ message: "Password must be at least 8 characters.", buttons: ["OK"] });
            return;
        }
        if (password !== confirmPassword) {
            presentAlert({ message: "Passwords do not match.", buttons: ["OK"] });
            return;
        }

        try {
            await actionToResetUserPassword(user.id, password);
            presentAlert({ message: "Password reset successfully!", buttons: ["OK"] });
            setShowResetAlert(false);
            setPassword("");
            setConfirmPassword("");
        } catch (err) {
            presentAlert({ message: "Failed to reset password.", buttons: ["OK"] });
        }
    };

    const callFunctionToDeletedata = (id) => {
        presentAlert({
            header: 'Are you sure?',
            cssClass: 'custom_site_alert_toast',
            message: 'You want to delete this user.',
            buttons: [
                { text: 'Cancel', role: 'cancel' },
                {
                    text: 'Delete',
                    role: 'confirm',
                    handler: async () => {
                        await actionToDeleteUserPostData(id);
                    }
                },
            ],
        });
    };

    const handlePasswordResetPopup = (user) => {
        setEditUsersData(user);
        setShowResetAlert(true);
    };

    const callFunctionToSetEdit = (user) => {
        setDataForEditing(user);
        setShowAddUserModalPopup(true);
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            setIsLoading(true);
            await actionToChangeUserRole(userId, newRole);
        } catch (err) {
            console.error("Role change failed", err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPageData = useCallback((page) => {
        const newOffset = (page - 1) * ITEMS_PER_PAGE;
        setCurrentPage(page);
        actionToGetUsersByFilter(false, { offset: newOffset });
    }, []);

    useEffect(() => {
        fetchPageData(1);
    }, [fetchPageData]);

    const handlePrev = () => {
        if (currentPage > 1) {
            fetchPageData(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < pageCount) {
            fetchPageData(currentPage + 1);
        }
    };


    const downloadUsersExcel = async () => {
        let exportUsersData = [];
        try {
            exportUsersData = await actionToGetAllUsersForExport({
                search_text: debouncedQ,
                totalCount,
            });
        } catch (error) {
            console.error("Failed to fetch users for export", error);
        }

        const sourceData = exportUsersData?.length ? exportUsersData : usersData;

        if (!Array.isArray(sourceData) || sourceData.length === 0) {
            console.warn("No users to export");
            return;
        }

        const roleLabel = (r) => (r === 1 ? "Admin" : r === 2 ? "User" : (r ?? ""));
        const yn = (v) => (v ? "Yes" : "No");
        const fmtDate = (d) =>
            d ? new Date(d).toLocaleString("en-GB", { hour12: false }) : "";

        // sort by ID (string-safe)
        const sorted = [...sourceData].sort((a, b) =>
            String(a.id).localeCompare(String(b.id))
        );

        // Map only fields you want in the export (omitting password/otp)
        const rows = sorted.map((u) => ({
            ID: u.id ?? "",
            Name: u.name ?? "",
            Email: u.email ?? "",
            "Phone Number": u.phone_number ?? "",
            Role: roleLabel(u.role),
            "Is Active": yn(u.is_active),
            "Email Approved": yn(u.is_email_approved),
            "Job Portal Role": u.job_portal_role ?? "",
            "Matrimonial Portal Role": u.matrimonial_portal_role ?? "",
            "Ref No": u.ref_no ?? "",
            Designation: u.designation ?? "",
            "Identity ID": u.identity_id ?? "",
            "Profile Image URL": u.profile_image_url ?? "",
            "Created At": fmtDate(u.created_at),
        }));

        // JSON -> worksheet
        const ws = XLSX.utils.json_to_sheet(rows);

        // Auto-filter over the full used range
        const range = XLSX.utils.decode_range(ws["!ref"]);
        ws["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

        // Freeze the header row
        ws["!freeze"] = { xSplit: 0, ySplit: 1 };

        // Auto column widths
        const headers = Object.keys(rows[0] || {});
        ws["!cols"] = headers.map((h, i) => {
            const maxLen = Math.max(
                h.length,
                ...rows.map((r) => String(r[h] ?? "").length)
            );
            // rough px -> Excel width; add padding
            const wch = Math.min(Math.max(10, maxLen + 2), 60);
            return { wch };
        });

        // Workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Users");

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
        });

        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        saveAs(blob, `users_${today}.xlsx`);
    };

    const reloadUserData = ()=>{
        actionToGetUsersByFilter(true, { search_text:debouncedQ, offset: 0 });
    }

    return (
        <IonPage>
            <IonContent className="ion-padding news-letter-content-page">
                <div className="admin_inner_tab_container">
                    <div className="header-row">
                        <h2 className="newsletter-heading">Users ({totalCount})</h2>

                        {/* === NEW: Debounced Searchbar (only logs debounced value) === */}
                        <div className={"in_right_bar_section_admin"}>
                        <IonSearchbar
                            value={q}
                            placeholder="Search users..."
                            autocomplete="off"
                            debounce={0} // we handle debounce ourselves
                            onIonInput={(e) => setQ((e.detail.value || '').trimStart())}
                            style={{ maxWidth: 360 }}
                        />

                        {usersData?.length > 0 ? (
                                <button className="download-button" onClick={downloadUsersExcel}>
                                    <IonIcon icon={downloadOutline}  />
                                    <span>Download</span>
                                </button>
                            ) :""
                        }

                        <IonIcon
                            className="add_news_icon addnews-btn"
                            onClick={() => setShowAddUserModalPopup(true)}
                            icon={addOutline}
                        />
                        </div>
                    </div>

                    <div className="newsletter-table-container">
                        <table className="newsletter-table user-data-table">
                            <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Role</th>
                                <th>Membership</th>
                                <th>Identity Id</th>
                                <th>Action</th>
                            </tr>
                            </thead>
                            <tbody>
                            {(loading) ?
                                <>
                                    {[...Array(4)].map((_, i) => (
                                        <tr key={i}>
                                            <td><FacebookLoader type={"facebookStyle"} item={1} /></td>
                                            <td><FacebookLoader type={"facebookStyle"} item={1} /></td>
                                            <td><FacebookLoader type={"facebookStyle"} item={1} /></td>
                                            <td><FacebookLoader type={"facebookStyle"} item={1} /></td>
                                            <td><FacebookLoader type={"facebookStyle"} item={1} /></td>
                                        </tr>
                                    ))}
                                </>
                                : (usersData?.length > 0) ? (
                                    usersData.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.name}</td>
                                            <td>{item.email || "-"}</td>
                                            <td>{item.phone_number}</td>
                                            <td>
                                                <IonSelect
                                                    value={item.role}
                                                    placeholder="Select Role"
                                                    height="36px"
                                                    interface="popover"
                                                    onIonChange={(e) => handleRoleChange(item.id, e.detail.value)}
                                                >
                                                    <IonSelectOption value={1}>Admin</IonSelectOption>
                                                    <IonSelectOption value={2}>User</IonSelectOption>
                                                </IonSelect>
                                            </td>
                                            <td onClick={() => setEditMemberShipData(item)}>{item?.membership_type ? item?.membership_type : 'N/A'}
                                                <IonIcon
                                                icon={pencil}
                                                className={"admin_delete_button_icon pencil_icon"}/>
                                            </td>
                                            <td>{item?.identity_id}</td>
                                            <td className={"action_td"}>
                                                <IonIcon
                                                    icon={pencil}
                                                    onClick={() => callFunctionToSetEdit(item)}
                                                    className={"admin_delete_button_icon pencil_icon"}
                                                />
                                                <IonIcon
                                                    icon={lockOpenOutline}
                                                    onClick={() => handlePasswordResetPopup(item)}
                                                    className={"admin_delete_button_icon lock-icon"}
                                                />
                                                <IonIcon
                                                    icon={trash}
                                                    onClick={() => callFunctionToDeletedata(item?.id)}
                                                    className={"admin_delete_button_icon"}
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '1rem' }}>
                                            No users found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="pagination-controls">
                        <button
                            type="button"
                            className="pagination_button"
                            disabled={currentPage === 1}
                            onClick={handlePrev}
                        >
                            <IonIcon icon={chevronBack} />
                        </button>
                        <span className="page-info">
                          Page {currentPage} of {pageCount}
                        </span>
                        <button
                            type="button"
                            className="pagination_button"
                            disabled={currentPage === pageCount}
                            onClick={handleNext}
                        >
                            <IonIcon icon={chevronForward} />
                        </button>
                    </div>
                </div>
            </IonContent>

            {/* Password Reset Alert */}
            <IonAlert
                isOpen={showResetAlert}
                onDidDismiss={() => setShowResetAlert(false)}
                header={"Reset Password"}
                message={"Enter new password and confirm."}
                inputs={[
                    {
                        name: "password",
                        type: "password",
                        placeholder: "New Password (min 8 chars)",
                        attributes: { minLength: 8 },
                        value: password,
                        handler: (value) => setPassword(value),
                    },
                    {
                        name: "confirmPassword",
                        type: "password",
                        placeholder: "Confirm Password",
                        attributes: { minLength: 8 },
                        value: confirmPassword,
                        handler: (value) => setConfirmPassword(value),
                    },
                ]}
                buttons={[
                    {
                        text: "Cancel",
                        role: "cancel",
                        handler: () => {
                            setShowResetAlert(false);
                            setPassword("");
                            setConfirmPassword("");
                        },
                    },
                    {
                        text: "Reset",
                        handler: (data) => {
                            setPassword(data.password);
                            setConfirmPassword(data.confirmPassword);
                            setTimeout(() => handlePasswordReset(editUsersData), 100);
                            return false;
                        },
                    },
                ]}
            />
            <IonLoading
                isOpen={isLoading}
                className="loading_loader_spinner_container"
                message="Please Wait..."
            />
            <AddUserModal
                isOpen={showAddUserModalPopup}
                dataForEditing={dataForEditing}
                onClose={() => setShowAddUserModalPopup(false)}
                setIsLoading={setIsLoading}
                reloadUserData={reloadUserData}
            />
            <AddEditUserMembershipData
                isOpen={!!editMemberShipData?.id}
                dataForEditing={editMemberShipData}
                onClose={() => setEditMemberShipData(false)}
                setIsLoading={setIsLoading}
                reloadUserData={reloadUserData}
            />
        </IonPage>
    );
};

export default AdminUserManagementPage;
